/**
 * K5 — which fields of a saved login are encrypted, and with whose key
 * (docs/DESIGN_K5_credential-encryption.md §3, §6, §8).
 *
 * `sealCredentialFields` runs on the way out (behind the write switch) and
 * `openCredentialFields` on the way in (always). Neither knows about
 * screens or endpoints; the three services every screen already calls
 * (vaultService, loyaltyPointService, homeOwnershipInfo) wrap their
 * requests with them.
 *
 * Keys: the owner's own master key from the device cache, or, when the
 * record belongs to someone else (a successor reading after a verified
 * death), that owner's master key opened from the sealed copy by
 * TrustedPartyAccess. Only a value that carries the `fkc1:` prefix ever
 * asks for a key, so accounts with plaintext logins never see a prompt.
 */
import { CREDENTIAL_WRITES_ENCRYPTED } from '../../config/credentialEncryption';
import { getSignedInUserIdFromSession } from '../auth/currentUser';
import {
  currentRouteAsReturnTo,
  promptToSetRecoveryCode,
  promptToUnlockVault,
} from '../auth/vaultUnlockPrompt';
import { createLogger } from '../../utils/logger';
import {
  CredentialDecryptError,
  decryptField,
  deriveCredentialKey,
  encryptField,
  isCredentialCiphertext,
} from './CredentialCipher';
import {
  getCachedMasterKey,
  isWrappedMasterKeyEnrolled,
  RecoveryCodeRequiredError,
  VaultLockedError,
} from './MasterKeyService';
import {
  fetchAndOpenPrimaryMasterKey,
  TrustedPartyAccessDeniedError,
  TrustedPartyAccessNotReadyError,
} from './TrustedPartyAccess';

const logger = createLogger('credentialFields');

/** The Passwords task record (`passwords/{id}`). */
export const PASSWORD_RECORD_FIELDS = ['username', 'password', 'notes'] as const;
/** A loyalty account (`loyalty-points/{id}`). */
export const LOYALTY_RECORD_FIELDS = ['accountNumber', 'username', 'password', 'notes'] as const;
/** A portal login on a checklist item (`portal-credentials/{type}/{id}`). */
export const PORTAL_RECORD_FIELDS = [
  'username',
  'password',
  'accountNumber',
  'phonePasscode',
  'privateKey',
  'seedPhrase',
  'masterCode',
  'userCodes',
] as const;
/** Home ownership sends its login inside the module DTO. */
export const HOME_OWNERSHIP_DTO_FIELDS = ['username', 'password'] as const;

/** Shown in place of a value that carries the prefix but will not open. */
export const CANNOT_OPEN_PLACEHOLDER = 'Cannot be opened on this device';

type Rec = Record<string, unknown>;

/** What `openCredentialFields` adds when the key was not available. */
export interface LockedMarker {
  /** True when the sealed fields were blanked because no key was at hand. */
  locked?: boolean;
  /** Why: the reader's own vault is locked, or the owner never connected them. */
  lockedReason?: 'vault-locked' | 'not-connected';
}

export interface OpenOptions {
  /**
   * Whose record this is. Omitted or equal to the signed-in user: the own
   * master key. Anyone else: the sealed master key that owner left for the
   * reader (successor after a verified death).
   */
  ownerUserId?: string | null;
  /**
   * False for reads that only count or list records (the dashboard, the
   * checklist progress): a locked vault then blanks the secret fields and
   * marks the records, without asking for the Recovery Code. The web must
   * not ask at sign-in for something that does not need the key.
   */
  prompt?: boolean;
}

// masterKey → credentialKey, memory only. A rotated master key derives a
// new entry; sign-out clears it (clearCredentialKeyCache).
const derivedKeys = new Map<string, string>();

async function credentialKeyFromMaster(masterKey: string): Promise<string> {
  const cached = derivedKeys.get(masterKey);
  if (cached) return cached;
  const key = await deriveCredentialKey(masterKey);
  derivedKeys.set(masterKey, key);
  return key;
}

export function clearCredentialKeyCache(): void {
  derivedKeys.clear();
}

/**
 * The master key a save needs. Logins are kept only under a Recovery Code
 * (decision D1, 2026-09-25): an account without one gets
 * RecoveryCodeRequiredError, an enrolled account whose key is not on this
 * device gets VaultLockedError. Fails closed.
 */
export async function masterKeyForCredentialWrite(): Promise<string> {
  if (!(await isWrappedMasterKeyEnrolled())) {
    throw new RecoveryCodeRequiredError();
  }
  const key = await getCachedMasterKey();
  if (!key) throw new VaultLockedError();
  return key;
}

/**
 * Ask before a screen writes anything: true when a login can be saved now.
 * Otherwise the right prompt is shown (set a Recovery Code, or unlock this
 * device) with the current screen as the way back, and false is returned
 * so the screen leaves without saving half a record. Always true while the
 * write switch is off.
 */
export async function ensureCredentialWriteKey(): Promise<boolean> {
  if (!CREDENTIAL_WRITES_ENCRYPTED) return true;
  try {
    await masterKeyForCredentialWrite();
    return true;
  } catch (err) {
    if (err instanceof RecoveryCodeRequiredError) {
      promptToSetRecoveryCode(currentRouteAsReturnTo(), 'logins');
      return false;
    }
    if (err instanceof VaultLockedError) {
      promptToUnlockVault('your saved logins are', currentRouteAsReturnTo());
      return false;
    }
    throw err;
  }
}

async function credentialKeyForOwner(ownerUserId: string | null | undefined): Promise<string> {
  const me = await getSignedInUserIdFromSession();
  if (!ownerUserId || ownerUserId === me) {
    const master = await getCachedMasterKey();
    if (!master) throw new VaultLockedError();
    return credentialKeyFromMaster(master);
  }
  const master = await fetchAndOpenPrimaryMasterKey(ownerUserId);
  return credentialKeyFromMaster(master);
}

function sealedFieldsOf(record: Rec, fields: readonly string[]): string[] {
  return fields.filter((f) => isCredentialCiphertext(record[f]));
}

/**
 * Encrypt the listed fields of a record about to be saved. A no-op while
 * the write switch is off, for empty values, and for values that are
 * already ciphertext (a migration or an edit that kept a field as read).
 * Throws RecoveryCodeRequiredError / VaultLockedError for the screen to
 * offer the right flow (vaultUnlockPrompt.promptForVaultKey).
 */
export async function sealCredentialFields<T extends Rec>(
  record: T,
  fields: readonly string[],
): Promise<T> {
  if (!CREDENTIAL_WRITES_ENCRYPTED) return record;
  const pending = fields.filter((f) => {
    const v = record[f];
    return typeof v === 'string' && v !== '' && !isCredentialCiphertext(v);
  });
  if (pending.length === 0) return record;
  const key = await credentialKeyFromMaster(await masterKeyForCredentialWrite());
  const out: Rec = { ...record };
  for (const f of pending) {
    out[f] = await encryptField(record[f] as string, key);
  }
  return out as T;
}

// The unlock prompt is asked at most once per window, however many records
// a screen opens.
const PROMPT_WINDOW_MS = 30_000;
let lastPromptAt = 0;

function promptUnlockOnce(): void {
  const now = Date.now();
  if (now - lastPromptAt < PROMPT_WINDOW_MS) return;
  lastPromptAt = now;
  promptToUnlockVault('your saved logins are', currentRouteAsReturnTo());
}

/** Test hook: forget the prompt window. */
export function __resetCredentialPromptWindow(): void {
  lastPromptAt = 0;
}

function blankSealed<T extends Rec>(record: T, sealed: string[], reason: LockedMarker['lockedReason']): T & LockedMarker {
  const out: Rec = { ...record, locked: true, lockedReason: reason };
  for (const f of sealed) out[f] = '';
  return out as T & LockedMarker;
}

/**
 * Decrypt the listed fields of records that came back from the API. Records
 * without ciphertext pass through untouched. When the key is not available:
 *
 *   - own vault locked (web before the code is entered this visit, or a
 *     phone before its first unlock): fields blanked, `locked: true`, and
 *     the unlock prompt shown once, returning here afterwards;
 *   - a trusted party whose own vault is locked: the same;
 *   - a trusted party the owner never finished connecting: fields blanked,
 *     `lockedReason: 'not-connected'`, no prompt (nothing to enter).
 *
 * A value that carries the prefix but fails its MAC is replaced by
 * CANNOT_OPEN_PLACEHOLDER and logged without the value.
 */
export async function openManyCredentialFields<T extends Rec>(
  records: T[],
  fields: readonly string[],
  opts: OpenOptions = {},
): Promise<Array<T & LockedMarker>> {
  const sealedPerRecord = records.map((r) => sealedFieldsOf(r, fields));
  if (!sealedPerRecord.some((s) => s.length > 0)) return records;

  let key: string;
  try {
    key = await credentialKeyForOwner(opts.ownerUserId);
  } catch (err) {
    if (err instanceof VaultLockedError || err instanceof TrustedPartyAccessNotReadyError) {
      if (opts.prompt !== false) promptUnlockOnce();
      return records.map((r, i) => blankSealed(r, sealedPerRecord[i], 'vault-locked'));
    }
    if (err instanceof TrustedPartyAccessDeniedError) {
      return records.map((r, i) => blankSealed(r, sealedPerRecord[i], 'not-connected'));
    }
    throw err;
  }

  const out: Array<T & LockedMarker> = [];
  for (let i = 0; i < records.length; i += 1) {
    const sealed = sealedPerRecord[i];
    if (sealed.length === 0) {
      out.push(records[i]);
      continue;
    }
    const opened: Rec = { ...records[i] };
    for (const f of sealed) {
      try {
        opened[f] = await decryptField(records[i][f] as string, key);
      } catch (err) {
        if (!(err instanceof CredentialDecryptError)) throw err;
        logger.warn(`Saved login field "${f}" could not be opened: ${err.message}`);
        opened[f] = CANNOT_OPEN_PLACEHOLDER;
      }
    }
    out.push(opened as T & LockedMarker);
  }
  return out;
}

export async function openCredentialFields<T extends Rec>(
  record: T,
  fields: readonly string[],
  opts: OpenOptions = {},
): Promise<T & LockedMarker> {
  const [opened] = await openManyCredentialFields([record], fields, opts);
  return opened;
}
